import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { useNavigate } from "react-router-dom";
import generateCaptcha from "../../components/captcha";
import {
  LuSettings2,
  LuSave,
  LuCoins,
  LuIndianRupee,
  LuDollarSign,
  LuUsers,
  LuStar,
  LuShare2,
  LuMessageCircle,
  LuSend,
  LuShieldCheck,
  LuTicket,
  LuPuzzle,
  LuSlidersHorizontal,
  LuLink,
  LuBrain,
  LuGamepad2,
  LuFacebook,
  LuAppWindow,
  LuWallet,
} from "react-icons/lu";

/* ---------------- Reusable presentational pieces ---------------- */

const Toggle = ({ checked, onChange }) => (
  <label className="relative inline-flex items-center cursor-pointer shrink-0">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="sr-only peer"
    />
    <div className="w-12 h-6 bg-black/15 rounded-full peer-focus:ring-4 peer-focus:ring-teal-400/30 peer-checked:bg-teal-500 transition-colors duration-300 relative">
      <div
        className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300 ${
          checked ? "translate-x-6" : "translate-x-0"
        }`}
      />
    </div>
  </label>
);

const SectionHeader = ({ icon, title, desc }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-9 h-9 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div>
      <h2 className="text-base sm:text-lg font-bold text-main-font leading-tight">
        {title}
      </h2>
      {desc && <p className="text-xs text-main-subfont mt-0.5">{desc}</p>}
    </div>
  </div>
);

// light icon chip (subtle, reduces visual heaviness)
const Chip = ({ children }) => (
  <div className="w-10 h-10 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
    {children}
  </div>
);

// number value row — no per-field save button
const ValueField = ({ icon, label, desc, value, setValue }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white border border-main-border p-3">
    <Chip>{icon}</Chip>
    <div className="min-w-0 flex-1">
      <label className="font-semibold text-main-font text-sm block truncate">
        {label}
      </label>
      <p className="text-xs text-main-subfont truncate">{desc}</p>
    </div>
    <div className="w-24 shrink-0">
      <input
        type="number"
        value={value ?? ""}
        min="0"
        placeholder="0"
        onChange={(e) => setValue(Number(e.target.value))}
        className="ui-input w-full px-3 py-2 rounded-lg text-center font-bold"
      />
    </div>
  </div>
);

const ToggleTile = ({ label, value, handler, icon }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl bg-white border border-main-border p-3.5">
    <div className="flex items-center gap-3 min-w-0">
      <Chip>{icon}</Chip>
      <div className="min-w-0">
        <span className="font-semibold text-main-font text-sm block truncate">
          {label}
        </span>
        <span
          className={`text-xs font-medium ${
            value ? "text-brand-teal" : "text-red-600"
          }`}
        >
          {value ? "Enabled" : "Disabled"}
        </span>
      </div>
    </div>
    <Toggle checked={value} onChange={handler} />
  </div>
);

// reward range row — min / max / daily limit, no save button
const RangeRow = ({ icon, label, desc, start, setStart, end, setEnd, limit, setLimit }) => (
  <div className="rounded-xl bg-white border border-main-border p-4">
    <div className="flex items-center gap-3 mb-3">
      <Chip>{icon}</Chip>
      <div>
        <h3 className="font-semibold text-main-font text-sm">{label}</h3>
        <p className="text-xs text-main-subfont mt-0.5">{desc}</p>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[
        { lbl: "Min", v: start, set: setStart },
        { lbl: "Max", v: end, set: setEnd },
        { lbl: "Daily Limit", v: limit, set: setLimit },
      ].map((f) => (
        <div key={f.lbl}>
          <label className="text-[11px] uppercase tracking-wide text-main-subfont block mb-1.5">
            {f.lbl}
          </label>
          <input
            type="number"
            value={f.v}
            min="0"
            onChange={(e) => f.set(Number(e.target.value))}
            className="ui-input w-full px-2 py-2 rounded-lg text-center font-bold"
          />
        </div>
      ))}
    </div>
  </div>
);

// URL field — no save button
const UrlField = ({
  label,
  icon,
  value,
  setter,
  placeholder,
  coin,
  setCoin,
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-white border border-main-border p-3">
    <div className="flex items-center gap-3 sm:w-56 shrink-0">
      <Chip>{icon}</Chip>
      <label className="font-semibold text-main-font text-sm">{label}</label>
    </div>
    <input
      type="url"
      value={value}
      onChange={(e) => setter(e.target.value)}
      className="ui-input flex-1 px-3 py-2 rounded-lg min-w-0"
      placeholder={placeholder}
    />
    {setCoin && (
      <div className="flex items-center gap-2 shrink-0 sm:border-l sm:border-main-border sm:pl-3">
        <LuCoins className="text-brand-teal shrink-0" />
        <input
          type="number"
          min="0"
          value={coin ?? ""}
          placeholder="Coin"
          onChange={(e) => setCoin(Number(e.target.value))}
          className="ui-input w-24 px-3 py-2 rounded-lg text-center font-bold"
          title="Reward coins for this link"
        />
      </div>
    )}
  </div>
);

// plain text field (Facebook / App IDs) — no save button
const TextField = ({ label, value, setter, placeholder }) => (
  <div>
    <label className="block text-main-subfont font-medium mb-1.5 text-xs uppercase tracking-wide">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => setter(e.target.value)}
      className="ui-input w-full px-3 py-2.5 rounded-xl"
      placeholder={placeholder}
    />
  </div>
);

// vibrant limit tile — no save button
const colors = {
  blue: "from-blue-500 to-blue-700 border-blue-400/40",
  green: "from-green-500 to-green-700 border-green-400/40",
  orange: "from-orange-500 to-orange-700 border-orange-400/40",
  purple: "from-purple-500 to-purple-700 border-purple-400/40",
};

const LimitTile = ({ icon, label, desc, value, setValue, color }) => {
  const selectedColor = colors[color] || colors.blue;
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${selectedColor} p-4 shadow-lg hover:shadow-2xl transition-all duration-300`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-white/20 text-white flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="font-bold text-white leading-tight">{label}</h3>
          <p className="text-xs text-white/80 mt-0.5">{desc}</p>
        </div>
      </div>
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter limit"
        className="w-full px-3 py-2 rounded-xl bg-black/25 border border-white/20 text-white placeholder-white/60 text-center font-bold focus:border-white/60 focus:ring-2 focus:ring-white/20 outline-none transition-all"
      />
    </div>
  );
};


const Controller = () => {
  const [loader, setLoader] = useState(false);

  // Main Panel States
  const [coin_value_rs, setCoin_value_rs] = useState(1);
  const [coin_value_usd, setCoin_value_usd] = useState(1);

  // Game Range States
  const [Game_Scratch_Starting_rang, setGame_Scratch_Starting_rang] =
    useState(1);
  const [Game_Scratch_Ending_rang, setGame_Scratch_Ending_rang] = useState(2);
  const [Game_Scratch_limit, setGame_Scratch_limit] = useState(10);
  const [Game_TicTacToe_Starting_rang, setGame_TicTacToe_Starting_rang] =
    useState(1);
  const [Game_TicTacToe_Ending_rang, setGame_TicTacToe_Ending_rang] = useState(2);
  const [Game_TicTacToe_limit, setGame_TicTacToe_limit] = useState(10);

  const [pubscale_id, setPubscale_id] = useState("");
  const [geemee_id, setGeemee_id] = useState("");
  const [geemee_api_key, setGeemee_api_key] = useState("");
  const [Whatsapp_url, setWhatsapp_url] = useState("");
  const [Telegram_url, setTelegram_url] = useState("");
  const [Quiz_url, setQuiz_url] = useState("");
  const [Game_url, setGame_url] = useState("");
  const [Quiz_coin, setQuiz_coin] = useState(0);
  const [Game_coin, setGame_coin] = useState(0);

  const [facebook_app_id, setFacebook_app_id] = useState("");
  const [facebook_client_token, setFacebook_client_token] = useState("");
  const [coin_review, setCoin_review] = useState(1);

  const [Game_Scratch_Status, setGame_Scratch_Status] = useState(true);
  const [Game_TicTacToe_Status, setGame_TicTacToe_Status] = useState(true);
  // Withdraw methods the installed app shows
  const [w_upi, setW_upi] = useState(true);
  const [w_paypal, setW_paypal] = useState(true);
  const [w_binance, setW_binance] = useState(true);
  const [referral_coin, setReferral_coin] = useState(0);

  const [Referral_limit, setReferral_limit] = useState(1);
  const [Review_limit, setReview_limit] = useState(1);
  const [Whatsapp_limit, setWhatsapp_limit] = useState(1);
  const [Telegram_limit, setTelegram_limit] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.GetController();
      const { status, message, data } = resp;
      if (status) {
        setCoin_value_rs(data?.coin_value_rs);
        setCoin_value_usd(data?.coin_value_usd);
        setGame_Scratch_Starting_rang(data?.Game_Scratch_Starting_rang);
        setGame_Scratch_Ending_rang(data?.Game_Scratch_Ending_rang);
        setGame_Scratch_limit(data?.Game_Scratch_limit);
        setGame_TicTacToe_Starting_rang(data?.Game_TicTacToe_Starting_rang);
        setGame_TicTacToe_Ending_rang(data?.Game_TicTacToe_Ending_rang);
        setGame_TicTacToe_limit(data?.Game_TicTacToe_limit);
        setWhatsapp_url(data?.Whatsapp_url || "");
        setTelegram_url(data?.Telegram_url || "");
        setQuiz_url(data?.Quiz_url || "");
        setGame_url(data?.Game_url || "");
        setQuiz_coin(data?.Quiz_coin ?? 0);
        setGame_coin(data?.Game_coin ?? 0);
        setGame_Scratch_Status(data?.Game_Scratch_Status ?? true);
        setGame_TicTacToe_Status(data?.Game_TicTacToe_Status ?? true);
        setW_upi(data?.w_upi ?? true);
        setW_paypal(data?.w_paypal ?? true);
        setW_binance(data?.w_binance ?? true);
        setPubscale_id(data?.pubscale_id || "");
        setGeemee_id(data?.geemee_id || "");
        setGeemee_api_key(data?.geemee_api_key || "");
        setFacebook_app_id(data?.facebook_app_id);
        setFacebook_client_token(data?.facebook_client_token);
        setCoin_review(data?.coin_review);
        setReferral_coin(data?.referral_coin);
        setTelegram_limit(data?.Telegram_limit);
        setWhatsapp_limit(data?.Whatsapp_limit);
        setReview_limit(data?.Review_limit);
        setReferral_limit(data?.Referral_limit);
      } else {
        toast.error(message);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      navigate("/");
    } finally {
      setLoader(false);
    }
  };

  // Toggles save instantly (kept as-is)
  const handleCheckboxChange = async (name, value) => {
    const content = `You are about to update. To confirm, please solve this math problem.`;
    const isCaptchaValid = generateCaptcha(content);
    if (!isCaptchaValid) {
      return toast.error("Captcha answer is not correct", { autoClose: 2000 });
    }
    setLoader(true);
    try {
      const resp = await Calling.UpdateControllerStatus({
        name,
        status: !value,
      });
      if (resp?.status) {
        toast.success("Updated successfully");
        if (name === "exclusive_module") {
          window.location.reload();
        }
      } else {
        toast.error(resp.message || "Failed to update");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
      navigate("/");
    } finally {
      setLoader(false);
      fetchData();
    }
  };

  // ====== Single "Save All" — saves every input field in one shot ======
  const handleSaveAll = async () => {
    // Validations
    const nums = [
      coin_value_rs,
      coin_value_usd,
      referral_coin,
      coin_review,
      Referral_limit,
      Review_limit,
      Whatsapp_limit,
      Telegram_limit,
      Quiz_coin,
      Game_coin,
    ];
    if (nums.some((n) => Number(n) < 0)) {
      return toast.error("Coin / limit values must be 0 or more");
    }
    // The installed app divides by these (coins per ₹ / $) — below 1 it crashes on Home.
    if (Number(coin_value_rs) < 1 || Number(coin_value_usd) < 1) {
      return toast.error("Coin Rs and Coin USD must be 1 or more");
    }

    // Ranges need Min <= Max; invalid ranges are skipped (rest still saves)
    const scratchValid =
      Number(Game_Scratch_Starting_rang) <= Number(Game_Scratch_Ending_rang);
    const ticTacToeValid =
      Number(Game_TicTacToe_Starting_rang) <= Number(Game_TicTacToe_Ending_rang);
    const fbReady = !!(facebook_app_id && facebook_client_token);
    const skipped = [];
    if (!scratchValid) skipped.push("Scratch range (Min can't be > Max)");
    if (!ticTacToeValid) skipped.push("Captcha / TicTacToe range (Min can't be > Max)");
    if (!fbReady && (facebook_app_id || facebook_client_token)) {
      skipped.push("Facebook config (both fields required)");
    }

    const check = generateCaptcha(
      "You are about to save ALL controller settings. Solve this math problem to confirm"
    );
    if (!check)
      return toast.error("Answer is not correct", { autoClose: 2000 });

    // ⚡ Build ONE payload — saved in a single DB write (1 request, not ~17)
    const body = {
      coin_value_rs,
      coin_value_usd,
      referral_coin,
      coin_review,
      Referral_limit,
      Review_limit,
      Whatsapp_limit,
      Telegram_limit,
      Quiz_coin,
      Game_coin,
      Whatsapp_url,
      Telegram_url,
      Quiz_url,
      Game_url,
      pubscale_id,
      geemee_id,
      geemee_api_key,
    };
    if (scratchValid) {
      body.Game_Scratch_Starting_rang = Game_Scratch_Starting_rang;
      body.Game_Scratch_Ending_rang = Game_Scratch_Ending_rang;
      body.Game_Scratch_limit = Game_Scratch_limit;
    }
    if (ticTacToeValid) {
      body.Game_TicTacToe_Starting_rang = Game_TicTacToe_Starting_rang;
      body.Game_TicTacToe_Ending_rang = Game_TicTacToe_Ending_rang;
      body.Game_TicTacToe_limit = Game_TicTacToe_limit;
    }
    if (fbReady) {
      body.facebook_app_id = facebook_app_id;
      body.facebook_client_token = facebook_client_token;
    }

    setLoader(true);
    try {
      const resp = await Calling.SaveAllController(body);
      if (resp?.status) {
        if (skipped.length === 0) {
          toast.success("All settings saved successfully");
        } else {
          toast.success(`Saved. Skipped: ${skipped.join(", ")}`, {
            autoClose: 4000,
          });
        }
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Save all error:", error);
      toast.error("Failed to save settings");
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="md:ml-56 min-h-screen px-3 sm:px-4 md:px-6 pb-10 pt-20">
          {/* Sticky header with single Save All */}
          <div className="sticky top-16 z-30 mb-5">
            <div className="ui-card-solid px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 shrink-0">
                  <LuSettings2 className="text-xl" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold text-main-font leading-tight">
                    Controller Panel
                  </h1>
                  <p className="text-main-subfont text-xs sm:text-sm">
                    Manage all application settings in one place
                  </p>
                </div>
              </div>
              <button
                onClick={handleSaveAll}
                className="w-full sm:w-auto justify-center px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold rounded-xl shadow-lg shadow-teal-500/30 hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center gap-2"
              >
                <LuSave className="text-lg" />
                Save All Changes
              </button>
            </div>
          </div>

          <div className="space-y-9 mt-3">
            {/* Coin & Reward Values */}
            <div>
              <SectionHeader
                icon={<LuCoins className="text-lg" />}
                title="Coin & Reward Values"
                desc="Base coin conversion rates and reward amounts"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ValueField
                  icon={<LuIndianRupee className="text-lg" />}
                  label="Coin Rs"
                  desc="Base Rs coin value for transactions"
                  value={coin_value_rs}
                  setValue={setCoin_value_rs}
                />
                <ValueField
                  icon={<LuDollarSign className="text-lg" />}
                  label="Coin USD"
                  desc="Base USD coin value for transactions"
                  value={coin_value_usd}
                  setValue={setCoin_value_usd}
                />
                <ValueField
                  icon={<LuUsers className="text-lg" />}
                  label="Referral Coin"
                  desc="Reward coin amount for user referrals"
                  value={referral_coin}
                  setValue={setReferral_coin}
                />
                <ValueField
                  icon={<LuStar className="text-lg" />}
                  label="Coin Review"
                  desc="Required coin amount for review unlock"
                  value={coin_review}
                  setValue={setCoin_review}
                />
              </div>
            </div>

            {/* Share Limit Control */}
            <div>
              <SectionHeader
                icon={<LuShare2 className="text-lg" />}
                title="Share Limit Control"
                desc="Daily limits for share and task based earnings"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <LimitTile
                  icon={<LuUsers className="text-xl" />}
                  label="Referral Limit"
                  desc="Referral earning limit"
                  value={Referral_limit}
                  setValue={setReferral_limit}
                  color="blue"
                />
                <LimitTile
                  icon={<LuStar className="text-xl" />}
                  label="Review Limit"
                  desc="Review task limit"
                  value={Review_limit}
                  setValue={setReview_limit}
                  color="green"
                />
                <LimitTile
                  icon={<LuMessageCircle className="text-xl" />}
                  label="WhatsApp Limit"
                  desc="WhatsApp share task limit"
                  value={Whatsapp_limit}
                  setValue={setWhatsapp_limit}
                  color="orange"
                />
                <LimitTile
                  icon={<LuSend className="text-xl" />}
                  label="Telegram Limit"
                  desc="Telegram share task limit"
                  value={Telegram_limit}
                  setValue={setTelegram_limit}
                  color="purple"
                />
              </div>
            </div>

            {/* Feature Status (toggles save instantly) */}
            <div>
              <SectionHeader
                icon={<LuShieldCheck className="text-lg" />}
                title="Feature Status"
                desc="Enable or disable games and withdraw methods (saves instantly)"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ToggleTile
                  label="Scratch Game"
                  value={Game_Scratch_Status}
                  handler={() =>
                    handleCheckboxChange(
                      "Game_Scratch_Status",
                      Game_Scratch_Status
                    )
                  }
                  icon={<LuTicket className="text-lg" />}
                />
                <ToggleTile
                  label="Captcha / TicTacToe Game"
                  value={Game_TicTacToe_Status}
                  handler={() =>
                    handleCheckboxChange(
                      "Game_TicTacToe_Status",
                      Game_TicTacToe_Status
                    )
                  }
                  icon={<LuPuzzle className="text-lg" />}
                />
                <ToggleTile
                  label="UPI Withdraw"
                  value={w_upi}
                  handler={() => handleCheckboxChange("w_upi", w_upi)}
                  icon={<LuIndianRupee className="text-lg" />}
                />
                <ToggleTile
                  label="PayPal Withdraw"
                  value={w_paypal}
                  handler={() => handleCheckboxChange("w_paypal", w_paypal)}
                  icon={<LuDollarSign className="text-lg" />}
                />
                <ToggleTile
                  label="Binance Withdraw"
                  value={w_binance}
                  handler={() => handleCheckboxChange("w_binance", w_binance)}
                  icon={<LuWallet className="text-lg" />}
                />
              </div>
            </div>

            {/* Reward Ranges */}
            <div>
              <SectionHeader
                icon={<LuSlidersHorizontal className="text-lg" />}
                title="Reward Ranges"
                desc="Configure reward ranges and daily play limits per game"
              />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <RangeRow
                  icon={<LuTicket className="text-lg" />}
                  label="Scratch Game Range"
                  desc="Reward range and daily limit for scratch cards"
                  start={Game_Scratch_Starting_rang}
                  setStart={setGame_Scratch_Starting_rang}
                  end={Game_Scratch_Ending_rang}
                  setEnd={setGame_Scratch_Ending_rang}
                  limit={Game_Scratch_limit}
                  setLimit={setGame_Scratch_limit}
                />
                <RangeRow
                  icon={<LuPuzzle className="text-lg" />}
                  label="Captcha / TicTacToe Range"
                  desc="Reward range and daily limit for the Captcha game (TicTacToe slot)"
                  start={Game_TicTacToe_Starting_rang}
                  setStart={setGame_TicTacToe_Starting_rang}
                  end={Game_TicTacToe_Ending_rang}
                  setEnd={setGame_TicTacToe_Ending_rang}
                  limit={Game_TicTacToe_limit}
                  setLimit={setGame_TicTacToe_limit}
                />
              </div>
            </div>

            {/* External Links */}
            <div>
              <SectionHeader
                icon={<LuLink className="text-lg" />}
                title="External Links"
                desc="Public URLs used inside the application"
              />
              <div className="space-y-3">
                <UrlField
                  label="WhatsApp Group URL"
                  icon={<LuMessageCircle className="text-lg" />}
                  value={Whatsapp_url}
                  setter={setWhatsapp_url}
                  placeholder="https://chat.whatsapp.com/..."
                />
                <UrlField
                  label="Telegram Channel URL"
                  icon={<LuSend className="text-lg" />}
                  value={Telegram_url}
                  setter={setTelegram_url}
                  placeholder="https://t.me/..."
                />
                <UrlField
                  label="Quiz URL"
                  icon={<LuBrain className="text-lg" />}
                  value={Quiz_url}
                  setter={setQuiz_url}
                  placeholder="https://example.com/quiz"
                  coin={Quiz_coin}
                  setCoin={setQuiz_coin}
                />
                <UrlField
                  label="Game URL"
                  icon={<LuGamepad2 className="text-lg" />}
                  value={Game_url}
                  setter={setGame_url}
                  placeholder="https://example.com/game"
                  coin={Game_coin}
                  setCoin={setGame_coin}
                />
              </div>
            </div>

            {/* Integrations: Facebook + Application IDs */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div>
                <SectionHeader
                  icon={<LuFacebook className="text-lg" />}
                  title="Facebook Integration"
                  desc="Credentials used for the Facebook SDK"
                />
                <div className="space-y-4">
                  <TextField
                    label="Facebook App ID"
                    value={facebook_app_id}
                    setter={setFacebook_app_id}
                    placeholder="Enter Facebook App ID"
                  />
                  <TextField
                    label="Facebook Client Token"
                    value={facebook_client_token}
                    setter={setFacebook_client_token}
                    placeholder="Enter Facebook Client Token"
                  />
                </div>
              </div>

              <div>
                <SectionHeader
                  icon={<LuAppWindow className="text-lg" />}
                  title="Application IDs"
                  desc="Identifiers and keys for connected ad services"
                />
                <div className="space-y-4">
                  <TextField
                    label="Pubscale ID"
                    value={pubscale_id}
                    setter={setPubscale_id}
                    placeholder="Enter Pubscale ID"
                  />
                  <TextField
                    label="Geemee ID"
                    value={geemee_id}
                    setter={setGeemee_id}
                    placeholder="Enter Geemee ID"
                  />
                  <TextField
                    label="Geemee API Key"
                    value={geemee_api_key}
                    setter={setGeemee_api_key}
                    placeholder="Enter Geemee API Key"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Controller;
